import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredUser, getStoredMirror } from '../../hooks/useAuth';

const TIME_SLOTS = [
  { id: 'morning', label: '09:00 – 13:00', sub: 'Утро', icon: 'ri-sun-line' },
  { id: 'day', label: '13:00 – 18:00', sub: 'День', icon: 'ri-sun-foggy-line' },
  { id: 'evening', label: '18:00 – 22:00', sub: 'Вечер', icon: 'ri-moon-line' },
] as const;

type SlotId = (typeof TIME_SLOTS)[number]['id'];

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function isWeekend(d: Date): boolean {
  const wd = d.getDay(); // 0 = Вс, 6 = Сб
  return wd === 0 || wd === 6;
}

function sameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatHuman(d: Date): string {
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    weekday: 'long',
  });
}

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Europe/Moscow — UTC+3 круглый год: перехода на летнее время в РФ нет с 2014. */
const MSK_UTC_OFFSET_MIN = 3 * 60;

/**
 * Крайний срок подачи заявки на завтра — 16:00:00 МСК включительно.
 * Позже ближайшая доступная дата — послезавтра. Тот же порог продублирован
 * на бэке (`_portal_min_meeting_date` в redirect.py) — фронт тут только UX.
 */
const CUTOFF_SEC = 16 * 60 * 60;

/** «Сейчас» по Москве: у результата getHours()/getDate() отдают московские значения. */
function mskNow(): Date {
  const now = new Date();
  return new Date(now.getTime() + (MSK_UTC_OFFSET_MIN + now.getTimezoneOffset()) * 60_000);
}

function isAfterCutoff(nowMsk: Date): boolean {
  const sec = nowMsk.getHours() * 3600 + nowMsk.getMinutes() * 60 + nowMsk.getSeconds();
  return sec > CUTOFF_SEC;
}

/** Первый будний день начиная с d — выходные в календаре недоступны. */
function skipWeekend(d: Date): Date {
  const x = new Date(d);
  while (isWeekend(x)) x.setDate(x.getDate() + 1);
  return x;
}

interface MeetingLimits {
  /** Сегодня по МСК — точка «сегодня» в календаре и запрет листать назад. */
  today: Date;
  /** Самая ранняя дата заявки: завтра или послезавтра (см. CUTOFF_SEC). */
  minDate: Date;
  /** minDate, сдвинутая с выходных на понедельник — её показываем в подсказке. */
  firstSelectable: Date;
  afterCutoff: boolean;
}

function computeLimits(): MeetingLimits {
  const now = mskNow();
  const today = startOfDay(now);
  const afterCutoff = isAfterCutoff(now);
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() + (afterCutoff ? 2 : 1));
  return { today, minDate, firstSelectable: skipWeekend(minDate), afterCutoff };
}

/**
 * Лимиты пересчитываются раз в минуту: форму могут открыть в 15:59 и отправить в 16:05 —
 * без пересчёта календарь предлагал бы дату, которую бэкенд уже отвергнет.
 */
function useMeetingLimits(): MeetingLimits {
  const [limits, setLimits] = useState(computeLimits);
  useEffect(() => {
    const id = setInterval(() => {
      setLimits((prev) => {
        const next = computeLimits();
        return next.minDate.getTime() === prev.minDate.getTime() ? prev : next;
      });
    }, 60_000);
    return () => clearInterval(id);
  }, []);
  return limits;
}

/** Inline month calendar — доступны будни начиная с minDate (см. computeLimits). */
function Calendar({
  value,
  onSelect,
  today,
  minDate,
}: {
  value: Date | null;
  onSelect: (d: Date) => void;
  today: Date;
  minDate: Date;
}) {
  const [viewMonth, setViewMonth] = useState(() => {
    const base = value ?? minDate;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const days = useMemo(() => {
    const firstDay = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const startWeekday = (firstDay.getDay() + 6) % 7; // make Monday = 0
    const daysInMonth = new Date(
      viewMonth.getFullYear(),
      viewMonth.getMonth() + 1,
      0,
    ).getDate();

    const cells: (Date | null)[] = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d));
    }
    return cells;
  }, [viewMonth]);

  // Can't navigate before the current month
  const canGoPrev =
    viewMonth.getFullYear() > today.getFullYear() ||
    (viewMonth.getFullYear() === today.getFullYear() &&
      viewMonth.getMonth() > today.getMonth());

  return (
    <div className="bg-[#141417] border border-[#26262c] rounded-2xl p-4 w-full">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          disabled={!canGoPrev}
          onClick={() =>
            setViewMonth(
              new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1),
            )
          }
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#26262c] text-foreground-600 hover:text-white hover:border-[#33333a] disabled:opacity-25 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <span className="text-sm font-semibold text-white">
          {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
        </span>
        <button
          type="button"
          onClick={() =>
            setViewMonth(
              new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1),
            )
          }
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#26262c] text-foreground-600 hover:text-white hover:border-[#33333a] transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            className="h-8 flex items-center justify-center text-[11px] font-medium text-foreground-400"
          >
            {w}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const disabled = d < minDate || isWeekend(d);
          const selected = sameDay(d, value);
          const isToday = sameDay(d, today);
          return (
            <button
              key={toISODate(d)}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(d)}
              className={[
                'h-9 rounded-lg text-sm font-medium transition-all cursor-pointer relative',
                disabled
                  ? 'text-foreground-300 cursor-not-allowed'
                  : selected
                    ? 'bg-red-500 text-white'
                    : 'text-foreground-700 hover:bg-[#1E1E23]',
              ].join(' ')}
            >
              {d.getDate()}
              {isToday && !selected && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-red-400" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function MeetingPage() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const mirror = getStoredMirror();
  const { today, minDate, firstSelectable, afterCutoff } = useMeetingLimits();

  const [location, setLocation] = useState('');
  const [date, setDate] = useState<Date | null>(null);
  const [slot, setSlot] = useState<SlotId | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) navigate('/', { replace: true });
  }, [user, navigate]);

  // Порог 16:00 МСК (или полночь) мог пройти с открытой формой — снимаем ставшую недоступной дату.
  useEffect(() => {
    if (!submitted && date && date < minDate) {
      setDate(null);
      setError('Ближайшая доступная дата сдвинулась — выберите дату заново.');
    }
  }, [date, minDate, submitted]);

  const canSubmit = location.trim().length > 3 && !!date && !!slot;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !date || !slot || !user || submitting) return;
    setError('');
    setSubmitting(true);

    try {
      const res = await fetch('/api/portal/meeting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: user.username,
          mirror: mirror.id,
          location: location.trim(),
          date: toISODate(date),
          time_slot: slot, // 'morning' | 'day' | 'evening'
        }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        setError(data?.error ?? 'Не удалось отправить заявку. Попробуйте ещё раз.');
        return;
      }

      setSubmitted(true);
    } catch {
      setError('Нет связи с сервером. Проверьте интернет и попробуйте снова.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Shell onBack={() => navigate('/chat-operator')}>
        <div className="max-w-lg mx-auto text-center pt-10">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-500/10 border border-primary-500/20 mx-auto mb-6">
            <i className="ri-check-line text-3xl text-primary-300" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Заявка отправлена</h2>
          <p className="text-foreground-500 text-sm leading-relaxed mb-8">
            Ваша заявка на встречу с представителем Альфа-Банка зарегистрирована.
            Менеджер свяжется с вами для подтверждения деталей.
          </p>

          <div className="bg-[#0e0e11] border border-[#18181c] rounded-2xl p-6 text-left space-y-4 mb-8">
            <SummaryRow icon="ri-map-pin-line" label="Место встречи" value={location.trim()} />
            <SummaryRow
              icon="ri-calendar-event-line"
              label="Дата"
              value={date ? formatHuman(date) : ''}
            />
            <SummaryRow
              icon="ri-time-line"
              label="Время"
              value={TIME_SLOTS.find((s) => s.id === slot)?.label ?? ''}
            />
          </div>

          <button
            onClick={() => navigate('/chat-operator')}
            className="w-full bg-primary-600 hover:bg-primary-600 text-white font-semibold rounded-xl py-3.5 text-sm transition-all cursor-pointer"
          >
            Вернуться в кабинет
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell onBack={() => navigate('/chat-operator')}>
      <div className="max-w-xl mx-auto">
        {/* Bank header */}
        <div className="flex items-start gap-4 bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/20 rounded-2xl px-5 py-5 mb-8">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-red-500/15 border border-red-500/25 flex-shrink-0">
            <i className="ri-bank-line text-2xl text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white mb-1">
              Встреча с представителем Альфа-Банка
            </h2>
            <p className="text-foreground-500 text-sm leading-relaxed">
              Заполните детали встречи для оформления банковских реквизитов.
              Представитель приедет в удобное вам место и время.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-7">
          {/* Место встречи */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-2">
              <span className="text-red-400 mr-1">*</span>Место встречи
            </label>
            <p className="text-xs text-foreground-400 mb-3">
              Полный адрес: город, улица, дом, квартира/офис
            </p>
            <textarea
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              rows={3}
              placeholder="г. Москва, ул. Тверская, д. 10, кв. 25"
              className="w-full bg-[#141417] border border-[#26262c] rounded-xl px-4 py-3.5 text-sm text-foreground-800 placeholder-foreground-300 focus:outline-none focus:border-red-500 transition-colors resize-none"
            />
          </div>

          {/* Дата встречи */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-2">
              <span className="text-red-400 mr-1">*</span>Дата встречи
            </label>
            <p className="text-xs text-foreground-400 mb-1">
              Ближайшая доступная дата — {formatHuman(firstSelectable)}
            </p>
            <p className="text-xs text-foreground-300 mb-3">
              {afterCutoff
                ? 'Сейчас после 16:00 МСК — запись открыта с послезавтра. Встречи только по будням.'
                : 'До 16:00 МСК можно записаться на завтра, позже — только с послезавтра. Встречи только по будням.'}
            </p>
            <button
              type="button"
              onClick={() => setCalendarOpen((o) => !o)}
              className={[
                'w-full flex items-center gap-3 bg-[#141417] border rounded-xl px-4 py-3.5 text-sm transition-colors cursor-pointer',
                calendarOpen ? 'border-red-500' : 'border-[#26262c] hover:border-[#33333a]',
              ].join(' ')}
            >
              <span className="w-5 h-5 flex items-center justify-center text-red-400">
                <i className="ri-calendar-event-line text-base" />
              </span>
              <span className={date ? 'text-foreground-800' : 'text-foreground-300'}>
                {date ? formatHuman(date) : 'Выберите дату'}
              </span>
              <span className="ml-auto w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className={`ri-arrow-down-s-line transition-transform ${calendarOpen ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {calendarOpen && (
              <div className="mt-3">
                <Calendar
                  value={date}
                  today={today}
                  minDate={minDate}
                  onSelect={(d) => {
                    setDate(d);
                    setCalendarOpen(false);
                  }}
                />
              </div>
            )}
          </div>

          {/* Время встречи */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-2">
              <span className="text-red-400 mr-1">*</span>Время встречи
            </label>
            <p className="text-xs text-foreground-400 mb-3">
              Выберите удобный временной промежуток
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {TIME_SLOTS.map((s) => {
                const active = slot === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSlot(s.id)}
                    className={[
                      'flex flex-col items-center gap-2 rounded-xl px-3 py-4 transition-all cursor-pointer border',
                      active
                        ? 'bg-red-500/10 border-red-500 text-white'
                        : 'bg-[#141417] border-[#26262c] text-foreground-600 hover:border-[#33333a] hover:text-foreground-800',
                    ].join(' ')}
                  >
                    <i
                      className={`${s.icon} text-xl ${active ? 'text-red-400' : 'text-foreground-400'}`}
                    />
                    <span className="text-sm font-semibold">{s.label}</span>
                    <span className="text-[11px] text-foreground-400">{s.sub}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-400 flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className={[
              'w-full flex items-center justify-center gap-2 rounded-xl py-4 text-sm font-semibold transition-all',
              canSubmit && !submitting
                ? 'bg-red-500 hover:bg-red-400 text-white cursor-pointer'
                : 'bg-[#141417] border border-[#26262c] text-foreground-300 cursor-not-allowed',
            ].join(' ')}
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Отправляем…
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill" />
                Отправить заявку на встречу
              </>
            )}
          </button>
        </form>
      </div>
    </Shell>
  );
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#141417] text-red-400 flex-shrink-0">
        <i className={icon} />
      </span>
      <div className="min-w-0">
        <p className="text-xs text-foreground-400 mb-0.5">{label}</p>
        <p className="text-sm text-foreground-800 break-words">{value}</p>
      </div>
    </div>
  );
}

/** Page chrome matching the portal theme (neutral branding + back nav). */
function Shell({
  children,
  onBack,
}: {
  children: React.ReactNode;
  onBack: () => void;
}) {
  return (
    <div
      className="min-h-screen bg-[#000000] flex flex-col"
    >
      {/* Top bar */}
      <header className="flex items-center gap-4 px-6 lg:px-8 py-5 border-b border-[#18181c] sticky top-0 bg-[#000000] z-20">
        <button
          onClick={onBack}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-[#26262c] text-foreground-600 hover:text-white hover:border-[#33333a] transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-primary-500/10 border border-primary-500/20">
            <i className="ri-shield-user-line text-lg text-primary-300" />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-[11px] text-foreground-500 tracking-widest uppercase">Корпоративный</span>
            <span className="text-sm font-semibold text-white tracking-wide">Портал сотрудников</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-6 lg:px-10 py-10 overflow-y-auto">{children}</main>
    </div>
  );
}
